/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import MessageCompressor from './../../../messagescompressor';
import Message, { IMessage } from './../../../message';
import PacketOptionsMessage from './packetoptionsmessage';
import ParserUtils from './../parserutils';
import { IPacketParserOptions, Packet } from '../packetparser';

import NumberBytesPacketDataMessage from './numberbytespacketdatamessage';
import StringBytesPacketDataMessage from './stringbytespacketdatamessage';
import BytesBytesPacketDataMessage from './bytesbytespacketdatamessage';
import StringObjectPacketDataMessage from './stringobjectpacketdatamessage';
import NumberObjectPacketDataMessage from './numberobjectpacketdatamessage';
import ObjectObjectPacketDataMessage from './objectobjectpacketdatamessage';
import ObjectBufferPacketDataMessage from './objectbufferpacketdatamessage';
import StringStringPacketDataMessage from './stringstringpacketdatamessage';
import StringNumberPacketDataMessage from './stringnumberpacketdatamessage';
import NumberNumberPacketDataMessage from './numbernumberpacketdatamessag';
import NumberStringPacketDataMessage from './numberstringpacketdatamessag';
import PackageDataMessage, { IPackageDataMessageConstructor } from './packagedatamessage';
import { IDescriptorBody } from '../../../descriptor';

const PACKET_DATA_MESSAGES: Record<number, IPackageDataMessageConstructor> = /* #__PURE__ -- @preserve */ ( () => [
	NumberBytesPacketDataMessage,
	StringBytesPacketDataMessage,
	BytesBytesPacketDataMessage,
	StringObjectPacketDataMessage,
	NumberObjectPacketDataMessage,
	ObjectObjectPacketDataMessage,
	ObjectBufferPacketDataMessage,
	StringStringPacketDataMessage,
	StringNumberPacketDataMessage,
	NumberNumberPacketDataMessage,
	NumberStringPacketDataMessage
].reduce(
	// eslint-disable-next-line @typescript-eslint/naming-convention
	( result: Record<number, IPackageDataMessageConstructor>, PacketDataMessageConstructor: IPackageDataMessageConstructor ) => {
		result[ PacketDataMessageConstructor.TYPE ] = PacketDataMessageConstructor;

		return result;
	},
	{}
) )();

export interface IPacketMessage extends IMessage {
	type: number;
	id?: number;
	nsp?: string;
	data: Buffer;
	options?: Buffer;
}

export default class PacketMessage extends Message {
	public readonly options: PacketOptionsMessage;

	public static readonly TYPE: number = 10;

	public static readonly DESCRIPTOR_NAME: string = 'PacketMessage';

	public static readonly DESCRIPTOR: IDescriptorBody = /* #__PURE__ -- @preserve */ ( () => ( {
		fields: {
			type: {
				type: 'uint32',
				id: 1
			},
			id: {
				type: 'uint32',
				id: 2
			},
			nsp: {
				type: 'string',
				id: 3
			},
			data: {
				type: 'bytes',
				id: 4
			},
			options: {
				type: 'bytes',
				id: 5
			}
		}
	} ) )();

	public constructor(
		public readonly type: number,
		public readonly data: PackageDataMessage,
		public readonly id?: number,
		public readonly nsp: string = '/',
		options?: PacketOptionsMessage
	) {
		super();

		this.options = options ?? new PacketOptionsMessage();
	}

	public get packetData(): unknown[] {
		if ( this.data.data === undefined && this.data.data1 === undefined ) {
			return [];
		}

		if ( this.data.data && this.data.data1 === undefined ) {
			return [ this.data.data ];
		}

		return [ this.data.data, this.data.data1 ];
	}

	public toJSON(): IPacketMessage {
		const options: Buffer = MessageCompressor.encode( this.options );
		const data: Buffer = MessageCompressor.encode( this.data );

		return {
			type: this.type + ( this.data.constructor.TYPE * 10 ),
			id: this.id,
			nsp: this.nsp === '/' ? undefined : this.nsp,
			data,
			options
		};
	}

	public static fromJSON( data: IPacketMessage ): PacketMessage {
		const frameDataType: number = Math.floor( data.type / 10 );

		return new PacketMessage(
			data.type - frameDataType * 10,
			MessageCompressor.decode( data.data, PACKET_DATA_MESSAGES[ frameDataType ] ) as PackageDataMessage,
			data.id,
			data.nsp,
			data.options ?
				MessageCompressor.decode( data.options, PacketOptionsMessage ) as PacketOptionsMessage :
				new PacketOptionsMessage()
		);
	}

	/**
	 * Creates a PacketMessage.
	 */
	public static create( packet: Packet, options: IPacketParserOptions = {} ): PacketMessage {
		const frameDataType: number = ParserUtils.getPacketType(
			ParserUtils.getType( packet.data[ 0 ] ),
			// Use type from data[ 0 ] because messages are prepared to support two items in data.
			// In the case when data contains only one item use a message with slots for two items of the same type.
			ParserUtils.getType( packet.data[ 1 ] ?? packet.data[ 0 ] )
		);

		return new PacketMessage(
			packet.type,
			PACKET_DATA_MESSAGES[ frameDataType ].create( packet.data[ 0 ], packet.data[ 1 ] ),
			packet.id,
			packet.nsp,
			new PacketOptionsMessage( options.flags, options.rooms, options.except )
		);
	}
}
